import React, { FC } from 'react'
import { ACTION_TYPES, CloudService, CloudServiceKind, CloudServiceSource } from '@feedyou/utils'

import { Option } from '../../../../lib/options'
import { FeedbotSelect } from '../FeedbotSelect'
import { useSelectPortalRef } from '../../../SelectPortalProvider'
import { useCloudServices } from '../../../../features/cloudServices/hooks'

type Props = {
  action: ACTION_TYPES
  kind: CloudServiceKind
  source: CloudServiceSource

  value: string
  onChange(value: string | undefined): void
}

export const CloudServiceSelect: FC<Props> = ({ value, kind, source, action, onChange }) => {
  const portalRef = useSelectPortalRef()
  const { defaultByKind, defaultByAction, availableServices = [] } = useCloudServices(source)

  const defaultForKind = defaultByKind?.[kind]
  const defaultForAction = defaultByAction?.[action]
  const options = flattenCloudServices(kind, availableServices || [])
  const selectedOption = options.find(option => option.value === value)

  return (
    <FeedbotSelect
      value={selectedOption}
      options={options}
      isClearable={true}
      openMenuOnFocus={false}
      menuPortalTarget={portalRef}
      onChange={option => onChange(option?.value)}
      placeholder={
        defaultForAction
          ? getPlaceholder(
              value,
              defaultForAction?.name || defaultForAction?.key,
              defaultForAction?.unit as string
            )
          : getPlaceholder(
              value,
              defaultForKind?.name || defaultForKind?.key,
              defaultForKind?.unit as string
            )
      }
    />
  )
}

function flattenCloudServices(givenKind: CloudServiceKind, services: CloudService[]): Option[] {
  return services.flatMap(service => {
    if (givenKind !== service.kind) {
      return []
    }

    if (!Object.keys(service.units || {}).length) {
      return [{ value: service.key, label: service.name || service.key }]
    }

    return Object.entries(service.units!).map(
      ([unitKey]): Option => ({
        value: `${service.key}---${unitKey}`,
        label: formatServiceUnit(service.name || service.key, unitKey)
      })
    )
  })
}

function getPlaceholder(value: string, defaultService?: string, defaultUnit?: string): string {
  if (!value) return `Default • ${formatServiceUnit(defaultService, defaultUnit)}`
  return `INVALID • ${value}`
}

function formatServiceUnit(service?: string, unit?: string): string {
  if (!unit && !service) return 'Unknown'
  if (!unit) return String(service)
  if (!service) return String(formatUnit(unit))

  return `${formatUnit(unit)} • ${service}`
}

function formatUnit(unit: string = ''): string {
  // TODO make more versatile by specifing unit name in cloud service definition
  return unit.replace(/^gpt/i, 'GPT')
}
